<template>
  <section class="benefits-section">
    <div class="benefits-section__surface">
      <div class="benefits-section__header">
        <DsChip label="Beneficios" variant="orange" />

        <DsText as="body4" class="benefits-section__title">
          <strong>Mientras más constante seas, más recompensas obtienes</strong>
          <span> combina beneficios para maximizar tu ahorro.</span>
        </DsText>
      </div>

      <div class="benefits-section__media">
        <picture class="benefits-section__picture" aria-hidden="true">
          <source :srcset="benefitsDesktopImage" media="(min-width: 1208px)">
          <source :srcset="benefitsTabletImage" media="(min-width: 768px)">
          <img
            :src="benefitsMobileImage"
            alt=""
            class="benefits-section__image"
          >
        </picture>
      </div>

      <div class="benefits-section__groups">
        <div v-for="group in groups" :key="group.title" class="benefits-section__group">
          <DsText as="subtitle4" class="benefits-section__group-title">{{ group.title }}</DsText>

          <div class="benefits-section__items">
            <article v-for="item in group.items" :key="item.title" class="benefits-section__item">
              <div class="benefits-section__item-head">
                <span class="benefits-section__item-marker" aria-hidden="true" />
                <DsText as="subtitle7" class="benefits-section__item-title">{{ item.title }}</DsText>
              </div>

              <DsText as="body4" class="benefits-section__item-copy">{{ item.copy }}</DsText>
            </article>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import DsChip from '../atoms/DsChip.vue'
import DsText from '../atoms/DsText.vue'
import benefitsDesktopImage from '../../../assets/pagos_beneficios_desktop.png'
import benefitsMobileImage from '../../../assets/pagos_beneficios_mobile.png'
import benefitsTabletImage from '../../../assets/pagos_beneficios_tablet.png'

const groups = [
  {
    title: 'Al contratar',
    items: [
      {
        title: 'Buen historial',
        copy: 'Buró de crédito ≥ 685 al contratar'
      },
      {
        title: 'Domiciliación inicial',
        copy: 'hasta 5% descuento'
      }
    ]
  },
  {
    title: 'Durante tu financiamiento',
    items: [
      {
        title: 'Pago puntual',
        copy: '6 meses consecutivos pagando a tiempo'
      },
      {
        title: 'Pago puntual sostenido',
        copy: '+5% adicional'
      },
      {
        title: 'Domiciliación activa',
        copy: '2.5% descuento'
      }
    ]
  }
]
</script>

<style scoped>
.benefits-section__surface {
  background: #ffffff;
  border-radius: 32px;
  padding: 24px 16px;
  box-sizing: border-box;
}

.benefits-section__header {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 6px;
}

.benefits-section__header :deep(.ds-chip) {
  padding: 8px 12px;
  font-size: 12px;
  color: #8c4f0f;
  font-family: "Inter", sans-serif;
}

.benefits-section__title {
  color: #111111;
  font-size: 16px;
  font-family: "Inter", sans-serif;
  line-height: 1.1;
  max-width: 26ch;
}

.benefits-section__title :deep(strong) {
  font-weight: var(--ds-h7-weight);
}

.benefits-section__media {
  position: relative;
  aspect-ratio: 248 / 248;
  margin: 18px 6px 0;
  border-radius: 20px;
  overflow: hidden;
  background: #e6e6e6;
}

.benefits-section__picture {
  display: block;
  width: 100%;
  height: 100%;
}

.benefits-section__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.benefits-section__callout {
  position: absolute;
  top: 18px;
  left: 14px;
  max-width: 152px;
  padding: 10px 12px;
  border-radius: 12px 12px 12px 2px;
  background: #eff9f0;
  box-shadow: 0 10px 18px rgba(38, 29, 73, 0.12);
}

.benefits-section__callout-copy {
  color: var(--ds-purple-900);
  font-size: 12px;
  font-weight: 400;
  font-family: "Inter", sans-serif;
  line-height: 1.15;
}

.benefits-section__callout-copy :deep(strong) {
  font-weight: 700;
}

.benefits-section__groups {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 18px 6px 0;
}

.benefits-section__group {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.benefits-section__group-title {
  color: #111111;
  font-size: 24px;
  font-weight: 700;
  font-family: "Bebas Neue Pro", sans-serif;
  line-height: 1;
}

.benefits-section__items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.benefits-section__item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.benefits-section__item-head {
  display: flex;
  align-items: center;
  gap: 6px;
}

.benefits-section__item-marker {
  width: 11px;
  height: 3px;
  border-radius: 999px;
  background: #4c47ef;
  flex: 0 0 auto;
}

.benefits-section__item-title {
  color: var(--ds-purple-900);
  font-size: 16px;
  font-weight: 600;
  font-family: "Inter", sans-serif;
  line-height: 1.15;
}

.benefits-section__item-copy {
  max-width: 19ch;
  color: #111111;
  font-size: 16px;
  font-weight: 400;
  font-family: "Inter", sans-serif;
  line-height: 1.1;
}

@media (min-width: 768px) {
  .benefits-section__surface {
    padding: 24px;
  }

  .benefits-section__header {
    gap: 32px;
    padding: 0;
  }

  .benefits-section__title {
    max-width: none;
    line-height: 24px;
  }

  .benefits-section__media {
    height: 256px;
    aspect-ratio: auto;
    margin: 24px 0 0;
    border-radius: 24px;
  }

  .benefits-section__callout {
    top: 18px;
    left: 18px;
    max-width: 208px;
    padding: 12px 14px;
  }

  .benefits-section__callout-copy {
    font-size: 16px;
    line-height: 1.15;
  }

  .benefits-section__groups {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
    padding: 24px 0 0;
  }

  .benefits-section__group {
    gap: 16px;
  }

  .benefits-section__items {
    gap: 24px;
  }

  .benefits-section__item {
    gap: 4px;
  }

  .benefits-section__item-copy {
    max-width: none;
    line-height: 1.2;
  }
}

@media (min-width: 1208px) {
  .benefits-section {
    max-width: 962px;
    width: 100%;
    margin: 0 auto;
  }

  .benefits-section__surface {
    border-radius: 32px;
    padding: 24px;
  }

  .benefits-section__header {
    gap: 32px;
    padding: 0;
  }

  .benefits-section__title {
    max-width: none;
  }

  .benefits-section__media {
    height: 344px;
    aspect-ratio: auto;
    margin: 24px 0 0;
    border-radius: 24px;
  }

  .benefits-section__callout {
    top: 30px;
    left: 98px;
    max-width: 272px;
    padding: 12px 14px;
    border-radius: 12px;
  }

  .benefits-section__callout-copy {
    font-size: 16px;
    line-height: 1.15;
  }

  .benefits-section__groups {
    gap: 24px;
    padding: 24px 0 0;
  }

  .benefits-section__group {
    gap: 16px;
  }

  .benefits-section__items {
    gap: 24px;
  }

  .benefits-section__item {
    gap: 5px;
  }

  .benefits-section__item-head {
    gap: 8px;
  }

  .benefits-section__item-marker {
    width: 12px;
    height: 3px;
  }

  .benefits-section__item-copy {
    max-width: none;
    line-height: 1.2;
  }
}
</style>
